import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import React, {useCallback, useState} from 'react';

import * as ViewHooks from '../../../state/views/hooks';
import * as PanelBankSectionConfigActions from '../../../state/views/panelBankSectionConfig/actions';
import type * as PanelBankSectionConfigTypes from '../../../state/views/panelBankSectionConfig/types';
import {useIsMobile} from '../../../util/panelbankFlow';
import {type ImageExportType} from '../../../util/panelExport';
import {type getPanelSpec} from '../../../util/panelTypes';
import {PanelDeleteMenuItem} from '../../Panel/common/PanelDeleteMenuItem';
import {PanelDuplicateMenuItem} from '../../Panel/common/PanelDuplicateMenuItem';
import {PanelOverflowMenu} from '../../Panel/common/PanelOverflowMenu';
import {usePanelContext} from '../../Panel/PanelContextProvider';
import {PanelExportMenu} from '../../Panel/PanelExportModal/PanelExportMenu';
import {PanelExportModalContainer} from '../../Panel/PanelExportModal/PanelExportModalContainer';
import {type ExportType} from '../../Panel/types';
import {usePanelsSharedRefsContext} from '../../Views/contexts/PanelsSharedRefsContext';

type ReportPanelOverflowMenuProps = {
  isMenuOpen: boolean;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  panelSpec: ReturnType<typeof getPanelSpec>;
  setIsMenuOpen: (isMenuOpen: boolean) => void;
};

export const ReportPanelOverflowMenu = ({
  isMenuOpen,
  panelBankSectionConfigRef,
  panelSpec,
  setIsMenuOpen,
}: ReportPanelOverflowMenuProps) => {
  const isMobileLayout = useIsMobile();
  const {panelBankConfigRef, workspaceSettingsRef} =
    usePanelsSharedRefsContext();
  const {panelRef, trackPanelControls, isReadOnly} = usePanelContext();

  const [exportType, setExportType] = useState<ExportType>();
  const [imageExportType, setImageExportType] = useState<ImageExportType>();
  const unsetExportType = useCallback(() => setExportType(undefined), []);

  const duplicatePanel = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.duplicatePanel
  );

  const deletePanel = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.deletePanel
  );
  const onRemovePanel = useCallback(() => {
    deletePanel(panelRef, panelBankConfigRef, workspaceSettingsRef);
    trackPanelControls({action: 'Panel deleted'});
  }, [
    deletePanel,
    panelRef,
    panelBankConfigRef,
    workspaceSettingsRef,
    trackPanelControls,
  ]);

  const onDuplicatePanel = useCallback(() => {
    duplicatePanel(panelRef, panelBankConfigRef);
    trackPanelControls({action: 'Panel duplicated'});
  }, [duplicatePanel, panelRef, panelBankConfigRef, trackPanelControls]);

  if (isMobileLayout || isReadOnly) {
    return null;
  }

  return (
    <>
      <PanelExportModalContainer
        exportType={exportType}
        imageExportType={imageExportType}
        panelType={panelSpec.type}
        unsetExportType={unsetExportType}
      />
      <PanelOverflowMenu isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen}>
        {/* The zIndex is needed for reports, otherwise the menu is hidden behind the
        panel */}
        <DropdownMenu.Content style={{zIndex: 1}} align="start">
          <PanelExportMenu
            panelSpec={panelSpec}
            setExportType={setExportType}
            setImageExportType={setImageExportType}
          />
          <DropdownMenu.Separator />
          <PanelDuplicateMenuItem onSelect={onDuplicatePanel} />
          <DropdownMenu.Separator />
          <PanelDeleteMenuItem onSelect={onRemovePanel} />
        </DropdownMenu.Content>
      </PanelOverflowMenu>
    </>
  );
};
